import { useRouter } from "next/router";
import { ReactElement, useEffect, useState } from "react";
import AccountSignupForm, {
  SignupUserData,
} from "components/Core/Auth/Signup/AccountSignupForm";
import BrandSignupForm from "components/Core/Auth/Signup/BrandSignupForm";
import SignupPlanStep from "components/Core/Auth/Signup/SignupPlanStep";
import SignupStepIndicator from "components/Core/Auth/Signup/SignupStepIndicator";
import LoginLayout from "components/Layout/LoginLayout";
import PageLoadingSpinner from "components/Shared/PageLoadingSpinner";
import HeadTitle from "components/Shared/HeadTitle";
import { useAuth } from "utils/auth";
import {
  useFetchCurrentUser,
  useSetCurrentBrandId,
} from "utils/currentUserHooks";
import { assertPresence } from "utils/assertions";
import { BRAND_INDEX } from "utils/links";
import { useRouteParam } from "utils/routeParamHooks";

enum SignupStep {
  Account = "Account",
  Brand = "Brand",
  Plan = "Plan",
}

const SIGNUP_STEPS = [SignupStep.Account, SignupStep.Brand, SignupStep.Plan];

export default function SignupPage() {
  const router = useRouter();
  const { handleLogin, isAuthReady, isAuthenticated } = useAuth();
  const setCurrentBrandId = useSetCurrentBrandId();

  const [sessionIdFromUrl] = useRouteParam("session_id");
  const [selectedPlanSlug] = useRouteParam("plan");

  const [createdBrandId, setCreatedBrandId] = useState<string>();
  const [isCompletingSignup, setIsCompletingSignup] = useState(false);

  const { data, loading } = useFetchCurrentUser();
  const signupBrandId = data?.me?.brands?.nodes[0]?.id ?? createdBrandId;

  const initialStep = (() => {
    if (!isAuthReady) return null;
    if (!isAuthenticated) return SignupStep.Account;
    if (signupBrandId) return SignupStep.Plan;
    if (loading) return null;
    return SignupStep.Brand;
  })();

  const [step, setStep] = useState<SignupStep | null>(initialStep);

  const isLoading =
    !step ||
    (isAuthenticated && loading && !signupBrandId) ||
    isCompletingSignup;

  useEffect(() => {
    if (!initialStep) return;
    setStep(initialStep);
  }, [initialStep]);

  const currentStepIndex = step ? SIGNUP_STEPS.indexOf(step) : 0;

  const handleAccountCompleted = async (signupData: SignupUserData) => {
    const auth = signupData.signupUser?.auth;

    assertPresence(auth, "User signup completed without returning auth data.");

    await handleLogin(auth, { redirectPath: null });
    setStep(SignupStep.Brand);
  };

  const handleBrandCompleted = async (brandId: string) => {
    setIsCompletingSignup(true);
    await setCurrentBrandId(brandId);
    setCreatedBrandId(brandId);
    setStep(SignupStep.Plan);
    setIsCompletingSignup(false);
  };

  const handleSignupCompleted = async (brandId: string) => {
    setIsCompletingSignup(true);
    await setCurrentBrandId(brandId);
    await router.replace(BRAND_INDEX);
  };

  const content = (() => {
    if (isLoading) {
      return <PageLoadingSpinner minHeight="12rem" />;
    }

    if (step === SignupStep.Account) {
      return <AccountSignupForm onCompleted={handleAccountCompleted} />;
    }

    if (step === SignupStep.Brand) {
      return <BrandSignupForm onCompleted={handleBrandCompleted} />;
    }

    if (!signupBrandId) {
      return <PageLoadingSpinner minHeight="12rem" />;
    }

    return (
      <SignupPlanStep
        brandId={signupBrandId}
        onContinue={() => handleSignupCompleted(signupBrandId)}
        selectedPlanSlug={selectedPlanSlug}
        sessionId={sessionIdFromUrl}
      />
    );
  })();

  return (
    <>
      <HeadTitle title="Sign Up" />

      {!isLoading && (
        <SignupStepIndicator currentStepIndex={currentStepIndex} />
      )}

      {content}
    </>
  );
}

SignupPage.getLayout = (page: ReactElement) => (
  <LoginLayout
    heading="Create Your Account"
    subheading="Set up your workspace and choose a plan."
  >
    {page}
  </LoginLayout>
);
